import { downloadFile } from "@/utils/download";
import { FaroDialog } from "@faro-lotv/flat-ui";
import { Alert, Box, LinearProgress, Link, Typography } from "@mui/material";
import { useEffect, useState, type JSX } from "react";
import { CYLINDER_DEVIATION_COLORS } from "./cylinder-surface";
import {
  SURFACE_REPORT_LOGO,
  SURFACE_REPORT_TITLE,
  type SurfaceReportStatus,
} from "./surface-report";

/**
 * @param props Report status, cancellation and retry callbacks.
 * @returns A shared-style report preview with download available only after generation.
 */
export function SurfaceReportDialog({
  status,
  onClose,
  onRetry,
}: {
  status: SurfaceReportStatus;
  onClose(): void;
  onRetry(): void;
}): JSX.Element {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (status.phase !== "ready") {
      setUrl(undefined);
      return;
    }
    const value = URL.createObjectURL(status.pdf);
    setUrl(value);
    return () => URL.revokeObjectURL(value);
  }, [status]);
  function confirm(): void {
    if (status.phase === "error") {
      onRetry();
    } else if (status.phase === "ready" && url) {
      const now = new Date();
      const timestamp = [
        [now.getFullYear(), now.getMonth() + 1, now.getDate()],
        [now.getHours(), now.getMinutes(), now.getSeconds()],
      ]
        .map((parts) =>
          parts.map((value) => String(value).padStart(2, "0")).join("-"),
        )
        .join("_");
      downloadFile(url, `${timestamp}_${SURFACE_REPORT_TITLE}.pdf`);
    }
  }
  return (
    <FaroDialog
      title={SURFACE_REPORT_TITLE}
      open={status.phase !== "idle"}
      variant="info"
      dark
      size="l"
      onClose={onClose}
      onCancel={onClose}
      cancelText={status.phase === "loading" ? "Cancel" : "Close"}
      onConfirm={status.phase === "loading" ? undefined : confirm}
      confirmText={status.phase === "error" ? "Retry" : "Download PDF"}
      isConfirmDisabled={status.phase === "ready" && !url}
      sx={{
        "& .MuiDialog-paper": {
          width: "min(900px, calc(100vw - 32px))",
          maxWidth: "none",
        },
      }}
    >
      {status.phase === "loading" && (
        <Box sx={{ py: 3 }}>
          <Typography role="status" sx={{ mb: 1 }}>
            {status.completed === status.total
              ? "Preparing PDF"
              : `Preparing images: ${status.completed} / ${status.total}`}
          </Typography>
          <LinearProgress
            aria-label="Report progress"
            variant="determinate"
            value={status.total ? (status.completed / status.total) * 100 : 0}
          />
        </Box>
      )}
      {status.phase === "error" && (
        <Alert severity="error">{status.message}</Alert>
      )}
      {status.phase === "ready" && (
        <Box
          aria-label="Report preview"
          sx={{
            maxHeight: "68vh",
            overflow: "auto",
            bgcolor: "white",
            color: "#222",
            p: { xs: 1.5, sm: 3 },
            fontFamily: "Helvetica, sans-serif",
          }}
        >
          <Box
            component="img"
            src={SURFACE_REPORT_LOGO}
            alt="FARO Sphere XG"
            sx={{
              width: 135,
              height: 34,
              objectFit: "contain",
              objectPosition: "left",
              mb: 1,
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontFamily: "inherit", overflowWrap: "anywhere" }}
          >
            {SURFACE_REPORT_TITLE}
          </Typography>
          <Typography sx={{ overflowWrap: "anywhere" }}>
            {status.report.projectName}
          </Typography>
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            {status.report.date}
          </Typography>
          {status.report.entries.map((entry, index) => (
            <Box
              component="article"
              key={entry.title}
              sx={{ borderTop: "1px solid #bbb", py: 2 }}
            >
              <Typography sx={{ fontWeight: 700 }}>{entry.title}</Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {entry.coverage}
              </Typography>
              <Box
                component="dl"
                sx={{ display: "flex", gap: 2, flexWrap: "wrap", my: 1 }}
              >
                {entry.dimensions.map(({ label, value }) => (
                  <Box key={label}>
                    <Typography component="dt" variant="caption">
                      {label}
                    </Typography>
                    <Typography
                      component="dd"
                      sx={{ m: 0, fontVariantNumeric: "tabular-nums" }}
                    >
                      ~{value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {entry.tolerance && (
                <Box sx={{ maxWidth: 360, mb: 1 }}>
                  <Typography variant="caption">Radial deviation</Typography>
                  <Box
                    role="img"
                    aria-label={`${entry.title} deviation scale`}
                    sx={{
                      height: 6,
                      background: `linear-gradient(90deg, ${CYLINDER_DEVIATION_COLORS.inside}, ${CYLINDER_DEVIATION_COLORS.fitted}, ${CYLINDER_DEVIATION_COLORS.outside})`,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      fontSize: 11,
                    }}
                  >
                    <span>Inside: -{entry.tolerance}</span>
                    <span>0</span>
                    <span>Outside: +{entry.tolerance}</span>
                  </Box>
                </Box>
              )}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  my: 1,
                }}
              >
                {[
                  { label: "Isolated selection", image: entry.isolated },
                  { label: "In context", image: entry.context },
                ].map(({ label, image }) => (
                  <Box component="figure" key={label} sx={{ m: 0 }}>
                    <Typography component="figcaption" variant="caption">
                      {label}
                    </Typography>
                    <Box
                      component="img"
                      src={image}
                      alt={`${entry.title}: ${label}`}
                      sx={{
                        width: "100%",
                        aspectRatio: "1.6",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Box>
              {entry.cylinderAnalysis && (
                <Box component="figure" sx={{ m: 0, my: 2 }}>
                  <Typography component="figcaption" variant="caption">
                    Axial orthographic view and deviation histogram
                  </Typography>
                  <Box
                    component="img"
                    src={entry.cylinderAnalysis}
                    alt={`${entry.title}: Axial view and radial-deviation histogram`}
                    sx={{
                      width: "100%",
                      aspectRatio: "1.6",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
              )}
              {entry.overviewMap && (
                <Box component="figure" sx={{ m: 0, my: 2 }}>
                  <Typography component="figcaption" variant="caption">
                    {entry.mapCaption ?? "Location on overview map"}
                  </Typography>
                  <Box
                    component="img"
                    src={entry.overviewMap}
                    alt={`${entry.title}: Overview map location`}
                    sx={{
                      width: "100%",
                      aspectRatio: "1.6",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
              )}
              <Link
                href={entry.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 13 }}
              >
                Open orthographic view in FARO Sphere XG
              </Link>
              <Typography
                variant="caption"
                component="p"
                sx={{ color: "#777", mb: 0 }}
              >
                Approximate measurements. Selection {index + 1} of{" "}
                {status.report.entries.length}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </FaroDialog>
  );
}
